import { useCallback, useEffect, useState } from 'react';
import { ArrowRight, EyeOff, Grid2X2, Loader2, RefreshCw, Sparkles } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { AuthModal } from '../components/AuthModal';
import { Navbar } from '../baselines/image';
import { getApplicationIcon } from '../apps/applications';
import { api, type PublicApplication } from '../services/api';
import { usePlatformSession } from '../platform/session';

export default function AppsPage() {
  const navigate = useNavigate();
  const { isLoggedIn, setUser } = usePlatformSession();
  const [showAuthModal, setShowAuthModal] = useState(false);

  const [applications, setApplications] = useState<PublicApplication[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);

  const loadApplications = useCallback(async () => {
    setLoading(true);
    setLoadError(false);
    try {
      const { data } = await api.getPublicApplications();
      setApplications(data);
    } catch {
      // 目录以服务端为唯一来源，失败时明确提示重试，不回退到本地全开清单
      setApplications([]);
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadApplications();
  }, [loadApplications]);

  return (
    <div className="min-h-screen bg-light-bg" style={{ paddingTop: '64px' }}>
      <Navbar
        onRedeemCredits={() => (isLoggedIn ? navigate('/user') : setShowAuthModal(true))}
        onLogin={() => setShowAuthModal(true)}
      />

      <AuthModal
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
        onSuccess={(nextUser) => setUser(nextUser)}
        defaultMode="login"
      />

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-14">
        <div className="max-w-2xl">
          <div className="inline-flex items-center gap-2 rounded-full bg-blue-50 px-3 py-1.5 text-sm font-medium text-accent-blue">
            <Grid2X2 className="w-4 h-4" />
            应用中心
          </div>
          <h1 className="mt-5 text-3xl sm:text-4xl font-semibold tracking-tight text-light-text">为你的创作目标选择应用</h1>
          <p className="mt-3 text-base sm:text-lg leading-8 text-light-muted">
            图片和视频生成提供基础创作能力；从这里进入适合当前场景的应用工作台。
          </p>
        </div>

        {loading ? (
          <div className="mt-16 flex items-center justify-center gap-2 text-sm text-light-muted">
            <Loader2 className="h-5 w-5 animate-spin" />
            正在加载应用目录...
          </div>
        ) : loadError ? (
          <div className="mt-10 rounded-2xl border border-dashed border-light-border bg-white p-10 text-center">
            <h2 className="text-lg font-medium text-light-text">应用目录加载失败</h2>
            <p className="mt-2 text-sm text-light-muted">请稍后重试，或联系管理员了解可用功能。</p>
            <button
              type="button"
              onClick={loadApplications}
              className="mt-5 inline-flex items-center gap-2 rounded-lg bg-accent-blue px-4 py-2 text-sm font-medium text-white hover:bg-blue-700"
            >
              <RefreshCw className="h-4 w-4" />
              重新加载
            </button>
          </div>
        ) : applications.length > 0 ? (
          <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {applications.map((application) => {
              const Icon = getApplicationIcon(application.appId);
              const disabled = !application.accessible;
              // 管理员视角：后台已停用但对管理员保留维护入口的应用
              const disabledButAdminAccessible = application.accessible && !application.enabled;
              return (
                <button
                  key={application.appId}
                  type="button"
                  onClick={() => navigate(application.path)}
                  disabled={disabled}
                  className={`group rounded-2xl border bg-white p-6 text-left shadow-sm transition-all ${
                    disabled
                      ? 'cursor-not-allowed border-light-border opacity-60'
                      : disabledButAdminAccessible
                        ? 'border-amber-200 hover:-translate-y-0.5 hover:border-amber-300 hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-amber-400 focus:ring-offset-2'
                        : 'border-light-border hover:-translate-y-0.5 hover:border-blue-200 hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-accent-blue focus:ring-offset-2'
                  }`}
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-blue-50 text-accent-blue">
                      <Icon className="h-6 w-6" />
                    </div>
                    {!disabled && (
                      <ArrowRight className="h-5 w-5 text-light-muted transition-transform group-hover:translate-x-1 group-hover:text-accent-blue" />
                    )}
                  </div>
                  <h2 className="mt-6 text-lg font-semibold text-light-text">{application.title}</h2>
                  <p className="mt-2 text-sm leading-6 text-light-muted">{application.description}</p>
                  {disabled ? (
                    <span className="mt-6 inline-flex items-center rounded-full bg-slate-100 px-3 py-1 text-xs text-slate-600">
                      已停用
                    </span>
                  ) : disabledButAdminAccessible ? (
                    <span className="mt-6 inline-flex items-center gap-1 rounded-full bg-amber-50 px-3 py-1 text-xs text-amber-700">
                      <EyeOff className="h-3.5 w-3.5" />
                      已停用 · 仅管理员可访问
                    </span>
                  ) : (
                    <span className="mt-6 inline-flex items-center gap-1 text-sm font-medium text-accent-blue">
                      进入应用
                      <Sparkles className="h-4 w-4" />
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        ) : (
          <div className="mt-10 rounded-2xl border border-dashed border-light-border bg-white p-10 text-center">
            <h2 className="text-lg font-medium text-light-text">暂无可用应用</h2>
            <p className="mt-2 text-sm text-light-muted">请稍后再试，或联系管理员了解可用功能。</p>
          </div>
        )}
      </main>
    </div>
  );
}
